import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import {
  FaThLarge,
  FaRoute,
  FaGraduationCap,
  FaTasks,
  FaHandshake,
  FaDollarSign,
  FaCogs,
  FaRobot,
  FaWallet,
  FaBullhorn,
  FaUsers,
  FaArchive,
  FaUser,
  FaBuilding,
  FaMoon,
  FaCreditCard,
  FaBell,
  FaPlug,
  FaCheck,
  FaArrowLeft,
} from 'react-icons/fa';
import { getAccountById, updateAccount } from '../../services/firestoreService';
import clientRoadmapService from '../../services/clientRoadmapService';
import { getPillarEmoji } from '../../utils/portalEmojis';
import KickoffAssessmentSections from '../../components/KickoffAssessmentSections';
import { fetchAccountPortalUsers, deriveProfileSetupStatus } from '../../services/kickoffProgressService';
import PortalBackground from '../../components/PortalBackground';

// `id` matches the TOUR_STOPS ids in PortalPendingStartGate, so a stop the
// client opened during Getting Started lights up the matching card here.
const PORTAL_GRID_ITEMS = [
  { id: 'dashboard', icon: FaThLarge, text: 'Dashboard' },
  { id: 'roadmap', icon: FaRoute, text: 'Roadmap' },
  { id: 'courses', icon: FaGraduationCap, text: 'Courses' },
  { id: 'tasks', icon: FaTasks, text: 'Tasks' },
  { id: 'community', icon: FaHandshake, text: 'Community' },
  { id: 'sales', icon: FaDollarSign, text: 'Sales' },
  { id: 'operations', icon: FaCogs, text: 'Operations' },
  { id: 'technology', icon: FaRobot, text: 'Technology' },
  { id: 'finance', icon: FaWallet, text: 'Finance' },
  { id: 'marketing', icon: FaBullhorn, text: 'Marketing' },
  { id: 'hr', icon: FaUsers, text: 'HR' },
  { id: 'collection', icon: FaArchive, text: 'Collection' },
];

const KickoffOverview = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { darkMode } = useTheme();
  const [account, setAccount] = useState(null);
  const [accountLoading, setAccountLoading] = useState(true);
  const [portalUsers, setPortalUsers] = useState([]);
  // Manual overrides on top of what the client's own data says. `undefined` for
  // a key means "trust the client data"; true/false is an explicit CRM call.
  const [checklist, setChecklist] = useState({ profile: {}, portalAreas: {} });
  const [kickoffStatus, setKickoffStatus] = useState('in_review'); // 'in_review' | 'complete'
  const [statusSaving, setStatusSaving] = useState(false);
  const [roadmapTemplates, setRoadmapTemplates] = useState([]);
  const [roadmapLoading, setRoadmapLoading] = useState(false);

  const profileStatus = useMemo(
    () => deriveProfileSetupStatus(account, portalUsers),
    [account, portalUsers]
  );
  const visitedStops = useMemo(
    () => new Set(account?.kickoffTourProgress?.visitedStops || []),
    [account?.kickoffTourProgress]
  );

  const persistChecklist = useCallback((next) => {
    setChecklist(next);
    if (!clientId) return;
    updateAccount(clientId, { kickoffChecklist: next }).catch((err) =>
      console.error('Error saving kickoff checklist:', err)
    );
  }, [clientId]);

  // Clicking a card flips it away from whatever it currently shows and records
  // that as an override, so a success manager can correct the auto-read.
  const toggleProfileKey = useCallback((key, currentlyDone) => {
    persistChecklist({
      ...checklist,
      profile: { ...checklist.profile, [key]: !currentlyDone },
    });
  }, [checklist, persistChecklist]);

  const togglePortalArea = useCallback((stopId, currentlyDone) => {
    persistChecklist({
      ...checklist,
      portalAreas: { ...checklist.portalAreas, [stopId]: !currentlyDone },
    });
  }, [checklist, persistChecklist]);

  useEffect(() => {
    const load = async () => {
      if (!clientId) {
        setAccount(null);
        setPortalUsers([]);
        setAccountLoading(false);
        return;
      }
      setAccountLoading(true);
      try {
        const accountData = await getAccountById(clientId);
        setAccount(accountData || null);
        setChecklist({
          profile: accountData?.kickoffChecklist?.profile || {},
          portalAreas: accountData?.kickoffChecklist?.portalAreas || {},
        });
        if (accountData?.kickoffStatus === 'complete' || accountData?.kickoffStatus === 'in_review') {
          setKickoffStatus(accountData.kickoffStatus);
        } else {
          setKickoffStatus('in_review');
        }
        // Profile setup lives on the client's own user docs, not the account.
        try {
          setPortalUsers(await fetchAccountPortalUsers(clientId));
        } catch (userErr) {
          console.warn('Could not load portal users for kickoff:', userErr);
          setPortalUsers([]);
        }
      } catch (err) {
        console.error('Error loading kickoff overview:', err);
        setAccount(null);
        setPortalUsers([]);
      } finally {
        setAccountLoading(false);
      }
    };
    load();
  }, [clientId]);

  useEffect(() => {
    const loadRoadmap = async () => {
      if (!clientId) {
        setRoadmapTemplates([]);
        setRoadmapLoading(false);
        return;
      }
      setRoadmapLoading(true);
      try {
        const clientRoadmap = await clientRoadmapService.getClientRoadmap(clientId);
        const visible = (clientRoadmap || []).filter(
          (t) => (t.status || '') !== 'hidden'
        );
        const sorted = [...visible].sort((a, b) => (a.order ?? a.allOrder ?? 0) - (b.order ?? b.allOrder ?? 0));
        setRoadmapTemplates(sorted);
      } catch (err) {
        console.error('Error loading client roadmap:', err);
        setRoadmapTemplates([]);
      } finally {
        setRoadmapLoading(false);
      }
    };
    loadRoadmap();
  }, [clientId]);

  const toggleKickoffStatus = useCallback(async () => {
    if (!clientId) return;
    const newStatus = kickoffStatus === 'complete' ? 'in_review' : 'complete';
    setStatusSaving(true);
    try {
      await updateAccount(clientId, { kickoffStatus: newStatus });
      setKickoffStatus(newStatus);
    } catch (err) {
      console.error('Error saving kickoff status:', err);
      alert('Error saving status. Please try again.');
    } finally {
      setStatusSaving(false);
    }
  }, [clientId, kickoffStatus]);

  const cardClass = `rounded-lg border p-4 transition-shadow ${darkMode ? 'bg-gray-800/50 border-gray-700' : 'bg-white border-gray-200'}`;
  const textMuted = darkMode ? 'text-gray-400' : 'text-gray-500';
  const textMain = darkMode ? 'text-gray-100' : 'text-gray-900';

  // Match Roadmap overview card styling helpers
  const getStatusColors = (status) => {
    switch (status) {
      case 'completed':
        return 'border-green-500 bg-green-100 dark:bg-green-900/40';
      case 'in-progress':
        return 'border-yellow-500 bg-yellow-100 dark:bg-yellow-900/40';
      case 'locked':
      case 'not-started':
        return 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800';
      default:
        return 'border-gray-300 dark:border-gray-600 bg-gray-100 dark:bg-gray-800';
    }
  };

  const getDepartmentEmoji = (item) => {
    if (!item) return '📋';
    if (item.emoji) return item.emoji;
    if (typeof item === 'string') {
      return getPillarEmoji(item);
    }
    const dept = (item.department && typeof item.department === 'string') ? item.department.toLowerCase() : '';
    return getPillarEmoji(dept);
  };

  const getDepartmentPillColor = (dept) => {
    const deptLower = dept?.toLowerCase() || '';
    const colorMap = {
      sales: 'bg-red-500 text-white',
      operations: 'bg-blue-500 text-white',
      finance: 'bg-green-500 text-white',
      technology: 'bg-purple-500 text-white',
      marketing: 'bg-orange-500 text-white',
      hr: 'bg-yellow-500 text-white',
      onboarding: 'bg-gray-500 text-white',
      general: 'bg-gray-500 text-white',
    };
    return colorMap[deptLower] || 'bg-gray-500 text-white';
  };

  const getDepartmentName = (dept) => {
    const deptLower = dept?.toLowerCase() || '';
    const nameMap = {
      sales: 'Sales',
      operations: 'Operations',
      finance: 'Finance',
      technology: 'Technology',
      marketing: 'Marketing',
      hr: 'HR',
      onboarding: 'Onboarding',
      general: 'General',
    };
    return nameMap[deptLower] || dept || 'General';
  };

  // One-line read of how far the client got through the Getting Started tour on
  // their pending-start dashboard.
  const tourProgress = account?.kickoffTourProgress;
  const tourSummary = (() => {
    const seen = visitedStops.size;
    if (!tourProgress || seen === 0) {
      return 'The client hasn\'t started their portal tour yet. Click a card to mark it covered.';
    }
    const total = tourProgress.totalStops || seen;
    const when = tourProgress.lastVisitedAt ? new Date(tourProgress.lastVisitedAt) : null;
    const whenLabel = when && !isNaN(when.getTime()) ? ` · last opened ${when.toLocaleDateString()}` : '';
    return `Client opened ${seen} of ${total} steps on their Getting Started tour${whenLabel}. Click a card to override.`;
  })();

  const profileCards = [
    { key: 'personalInfo', title: 'Personal Information', icon: FaUser },
    { key: 'companyInfo', title: 'Company Information', icon: FaBuilding },
    { key: 'darkMode', title: 'Dark Mode / Light Mode', icon: FaMoon },
    { key: 'billing', title: 'Billing', icon: FaCreditCard },
    { key: 'notifications', title: 'Notifications', icon: FaBell },
    { key: 'integrations', title: 'Integrations', icon: FaPlug },
  ];

  if (accountLoading) {
    return (
      <div className={`min-h-screen p-6 relative ${textMain}`}>
        <PortalBackground />
        <div className="relative max-w-4xl mx-auto flex items-center justify-center min-h-[200px]">
          <p className={textMuted}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!account) {
    return (
      <div className={`min-h-screen p-6 relative ${textMain}`}>
        <PortalBackground />
        <div className="relative max-w-4xl mx-auto text-center">
          <p className={textMuted}>Client not found.</p>
          <button type="button" onClick={() => navigate('/crm/fulfillment/kickoff')} className="mt-4 text-blue-600 dark:text-blue-400 hover:underline">
            Back to Kickoff list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 relative ${textMain}`}>
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto space-y-8">
        <header className="flex items-center justify-between flex-wrap gap-4">
          <button
            type="button"
            onClick={() => navigate('/crm/fulfillment/kickoff')}
            className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700 text-gray-300' : 'hover:bg-gray-100 text-gray-600'}`}
            title="Back to Kickoff list"
          >
            <FaArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="flex-1 text-2xl font-bold tracking-tight text-center">
            {account.company || 'Client'}
          </h1>
          <div className="w-20 flex justify-end">
            <button
              type="button"
              onClick={toggleKickoffStatus}
              disabled={statusSaving}
              className={`p-2 rounded-lg transition-colors ${
                kickoffStatus === 'complete'
                  ? 'text-green-600 hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-900/20'
                  : 'text-amber-600 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-900/20'
              } ${statusSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
              title={kickoffStatus === 'complete' ? 'Put into In Review' : 'Mark Complete'}
            >
              {kickoffStatus === 'complete' ? (
                <FaCheck className="w-5 h-5" />
              ) : (
                <FaTasks className="w-5 h-5" />
              )}
            </button>
          </div>
        </header>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <section className={cardClass}>
            <h2 className={`text-2xl font-semibold mb-3 text-center ${textMain}`}>Success Manager</h2>
            {account?.successManager ? (
              <div className="flex flex-col items-center text-center">
                <p className={`text-2xl font-semibold ${textMain}`}>{account.successManager}</p>
                {account.successManagerEmail && (
                  <p className="mt-1 truncate max-w-full">
                    <a href={`mailto:${account.successManagerEmail}`} className="text-blue-600 dark:text-blue-400 hover:underline text-sm">{account.successManagerEmail}</a>
                  </p>
                )}
                {account.successManagerPhone && (
                  <p className="mt-0.5">
                    <a href={`tel:${account.successManagerPhone}`} className="text-blue-600 dark:text-blue-400 hover:underline text-sm">{account.successManagerPhone}</a>
                  </p>
                )}
              </div>
            ) : (
              <p className={`text-center ${textMuted}`}>No success manager assigned yet.</p>
            )}
          </section>
          <section className={cardClass}>
            <h2 className={`text-2xl font-semibold mb-3 text-center ${textMain}`}>Monthly Check-In</h2>
            <p className={`text-center text-2xl font-semibold ${textMain}`}>1st of each month</p>
            <p className={`mt-1 text-center text-sm ${textMuted}`}>Sent automatically to all clients.</p>
          </section>
        </div>

        <section>
          <h2 className={`text-2xl font-semibold mb-1 text-center ${textMain}`}>Profile Setup</h2>
          <p className={`mb-4 text-center text-sm ${textMuted}`}>
            {portalUsers.length > 0
              ? `What the client has filled in so far (${portalUsers.length} portal ${portalUsers.length === 1 ? 'user' : 'users'}). Click a card to override.`
              : 'No portal users on this account yet — nothing to read from.'}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {profileCards.map((card) => {
              const Icon = card.icon;
              const status = profileStatus[card.key] || { done: false, detail: '' };
              const override = checklist.profile?.[card.key];
              const done = override === undefined ? status.done : override;
              const overridden = override !== undefined && override !== status.done;
              return (
                <button
                  key={card.key}
                  type="button"
                  onClick={() => toggleProfileKey(card.key, done)}
                  title={overridden ? 'Manually set — click to flip back' : 'Read from the client\'s profile'}
                  className={`${cardClass} flex items-center gap-3 text-left w-full ${
                    done ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-600' : ''
                  }`}
                >
                  <div className={`w-10 h-10 shrink-0 rounded-xl flex items-center justify-center bg-gray-100 dark:bg-gray-800`}>
                    <Icon className={darkMode ? 'text-gray-300' : 'text-gray-600'} />
                  </div>
                  <div className="min-w-0">
                    <span className="font-medium block">
                      {card.title}
                      {overridden && <span className={`ml-1 text-xs font-normal ${textMuted}`}>(manual)</span>}
                    </span>
                    <span className={`block text-xs ${textMuted}`}>{status.detail}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className={`text-2xl font-semibold mb-1 text-center ${textMain}`}>Portal Areas</h2>
          <p className={`mb-4 text-center text-sm ${textMuted}`}>
            {tourSummary}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {PORTAL_GRID_ITEMS.map((item) => {
              const Icon = item.icon;
              const override = checklist.portalAreas?.[item.id];
              const visited = visitedStops.has(item.id);
              const done = override === undefined ? visited : override;
              const overridden = override !== undefined && override !== visited;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => togglePortalArea(item.id, done)}
                  title={overridden ? 'Manually set — click to flip back' : visited ? 'Client opened this on their tour' : 'Not opened yet'}
                  className={`${cardClass} flex flex-col items-center justify-center py-5 gap-2 ${
                    done ? 'bg-green-100 border-green-300 dark:bg-green-900/30 dark:border-green-600' : ''
                  }`}
                >
                  <Icon className={`text-xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                  <span className="text-sm font-medium text-center">
                    {item.text}
                    {overridden && <span className={`block text-[11px] font-normal ${textMuted}`}>manual</span>}
                  </span>
                </button>
              );
            })}
          </div>
        </section>

        <section>
          <h2 className={`text-2xl font-semibold mb-4 text-center ${textMain}`}>Meetings</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className={`${cardClass} flex flex-col items-center justify-center py-5 gap-2`}>
              <FaHandshake className={`text-2xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              <span className="text-lg font-medium text-center">Breakthrough Sessions</span>
            </div>
            <div className={`${cardClass} flex flex-col items-center justify-center py-5 gap-2`}>
              <FaUser className={`text-2xl ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
              <span className="text-lg font-medium text-center">1 on 1 Meetings</span>
            </div>
          </div>
        </section>

        {clientId && account && <KickoffAssessmentSections accountId={clientId} account={account} darkMode={darkMode} />}

        <section>
          <h2 className={`text-2xl font-semibold mb-4 text-center ${textMain}`}>Roadmap</h2>
          {roadmapLoading ? (
            <p className={`text-center ${textMuted}`}>Loading roadmap...</p>
          ) : roadmapTemplates.length === 0 ? (
            <p className={`text-center ${textMuted}`}>No visible roadmap cards.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {roadmapTemplates.map((template) => (
                <div
                  key={template.clientRoadmapId ?? template.id}
                  className={`rounded-lg border-2 p-4 shadow-md transition-all hover:shadow-lg ${getStatusColors(template.status)}`}
                >
                  <div className="text-center flex flex-col h-full relative">
                    <div className="mb-3">
                      <span className="text-4xl">{getDepartmentEmoji(template)}</span>
                    </div>
                    <h3 className="font-bold text-xl text-gray-800 dark:text-white mb-4">
                      {template.title || 'Untitled'}
                    </h3>
                    <div className="mt-auto flex justify-center">
                      <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getDepartmentPillColor(template.department)}`}>
                        {getDepartmentName(template.department)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default KickoffOverview;
